import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mykzqjoq";

const helpOptions = [
  { value: "new-website", label: "New website" },
  { value: "website-redesign", label: "Website redesign" },
  { value: "google-ads", label: "Google Ads" },
  { value: "seo", label: "SEO" },
  { value: "other", label: "Other" },
];

export const LeadForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [helpWith, setHelpWith] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    website_url: "",
    message: "",
    help_with_other: "",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Get the display label for the selected option
    const selectedOption = helpOptions.find((opt) => opt.value === helpWith);
    const helpWithLabel = selectedOption?.label || helpWith;

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name.trim());
    formDataToSend.append("email", formData.email.trim());
    formDataToSend.append("phone", formData.phone.trim());
    formDataToSend.append("help_with", helpWithLabel);
    
    if (formData.business_name.trim()) {
      formDataToSend.append("business_name", formData.business_name.trim());
    }
    if (formData.website_url.trim()) {
      formDataToSend.append("website_url", formData.website_url.trim());
    }
    if (formData.message.trim()) {
      formDataToSend.append("message", formData.message.trim());
    }
    if (helpWith === "other" && formData.help_with_other.trim()) {
      formDataToSend.append("help_with_other", formData.help_with_other.trim());
    }

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        navigate(`/thank-you/?type=${encodeURIComponent(helpWithLabel)}`);
      } else {
        const data = await response.json();
        throw new Error(data.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Required Fields */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            maxLength={100}
            value={formData.name}
            onChange={handleInputChange}
            placeholder="John Smith"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            maxLength={255}
            value={formData.email}
            onChange={handleInputChange}
            placeholder="john@example.com"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            maxLength={20}
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(555) 123-4567"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="help_with">What can we help with? *</Label>
          <Select value={helpWith} onValueChange={setHelpWith} required>
            <SelectTrigger id="help_with">
              <SelectValue placeholder="Select an option…" />
            </SelectTrigger>
            <SelectContent>
              {helpOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Conditional "Other" input */}
      {helpWith === "other" && (
        <div className="space-y-2">
          <Label htmlFor="help_with_other">Tell us what you need *</Label>
          <Input
            id="help_with_other"
            name="help_with_other"
            type="text"
            required
            maxLength={200}
            value={formData.help_with_other}
            onChange={handleInputChange}
            placeholder="Describe your project..."
          />
        </div>
      )}

      {/* Business Name (Required) + Current Website (Optional) */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="business_name">Business Name *</Label>
          <Input
            id="business_name"
            name="business_name"
            type="text"
            required
            maxLength={100}
            value={formData.business_name}
            onChange={handleInputChange}
            placeholder="Green Lawn Pros LLC"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="website_url">Current Website</Label>
          <Input
            id="website_url"
            name="website_url"
            type="url"
            maxLength={255}
            value={formData.website_url}
            onChange={handleInputChange}
            placeholder="https://yoursite.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          name="message"
          required
          maxLength={1000}
          rows={3}
          value={formData.message}
          onChange={handleInputChange}
          placeholder="Tell us about your project or what you're looking for..."
        />
      </div>

      <Button 
        type="submit" 
        size="lg" 
        className="w-full bg-primary-dark hover:bg-[hsl(124,55%,18%)] text-white font-semibold py-6 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-[1px] active:translate-y-0 active:shadow-md transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary/30 disabled:bg-primary/40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0 min-h-[48px]"
        disabled={isSubmitting || !helpWith}
      >
        {isSubmitting ? "Sending..." : "Get Started"}
      </Button>
    </form>
  );
};
